/** Auth context — oidc-client-ts PKCE flow against Admin Cognito Pool. */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { loadAuthConfig, type AuthConfig } from "@/auth/config";

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  tier: "user" | "power_user" | "admin";
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => {},
  logout: () => {},
  tier: "user",
});

let _userManager: UserManager | null = null;

export function getUserManager(): UserManager | null {
  return _userManager;
}

function resolveTier(user: User | null, config: AuthConfig): "user" | "power_user" | "admin" {
  if (!user?.profile) return "user";

  // Try custom:license_tiers first (GATEWAY-SPEC-009/010 multi-product pattern)
  const tiersRaw = user.profile["custom:license_tiers"];
  if (tiersRaw) {
    try {
      const tiers = typeof tiersRaw === "string" ? JSON.parse(tiersRaw) : tiersRaw;
      const productTier = tiers?.[config.productId];
      if (productTier === "admin") return "admin";
      if (productTier === "power_user") return "power_user";
      if (productTier === "user") return "user";
    } catch { /* fall through to groups */ }
  }

  // Fallback: cognito:groups
  const raw = user.profile["cognito:groups"] ?? "";
  const groups = Array.isArray(raw) ? raw.join(",") : String(raw);
  if (groups.includes(config.adminGroupName)) return "admin";
  if (groups.includes(config.powerUserGroupName)) return "power_user";
  return "user";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState<AuthConfig | null>(null);

  useEffect(() => {
    loadAuthConfig().then((cfg) => {
      setConfig(cfg);

      if (!cfg.cognitoClientId) {
        setIsLoading(false);
        return;
      }

      const um = new UserManager({
        authority: `https://cognito-idp.ap-southeast-2.amazonaws.com/${cfg.cognitoUserPoolId}`,
        client_id: cfg.cognitoClientId,
        redirect_uri: `${window.location.origin}/callback`,
        post_logout_redirect_uri: window.location.origin,
        response_type: "code",
        scope: "openid email profile",
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({
          store: sessionStorage,
        }),
      });
      _userManager = um;

      if (window.location.pathname === "/callback") {
        um.signinRedirectCallback()
          .then((u) => {
            setUser(u);
            setIsLoading(false);
            window.location.replace("/");
          })
          .catch(() => setIsLoading(false));
        return;
      }

      um.getUser()
        .then((u) => {
          setUser(u);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    });
  }, []);

  const login = () => _userManager?.signinRedirect();
  const logout = () => {
    _userManager?.removeUser();
    if (config) {
      const logoutUrl = `${config.cognitoAuthDomain}/logout?client_id=${config.cognitoClientId}&logout_uri=${encodeURIComponent(window.location.origin)}`;
      window.location.replace(logoutUrl);
    }
  };

  const tier = config ? resolveTier(user, config) : "user";

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user && !user.expired,
        isLoading,
        login,
        logout,
        tier,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}
